import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useOnClickOutside } from '../../../../utils/functions/hooks';
import Loader from '../Loader';
import {
  ModalContainer,
  ModalDialog,
  ModalHeader,
  Title,
  ModalBody,
  ModalActions,
  ModalButton,
  ModalConfirmButton,
  PreventBodyScrolling,
  ModalCloseIcon,
  ModalFooterWrapper,
  ModalErrorText,
} from './Modal.style';

const Modal = props => {
  const {
    toggle,
    size,
    title,
    children,
    onClose,
    onCancelHandler,
    cancelActionLabel,
    onConfirmHandler,
    disableConfirmButton,
    confirmActionLabel,
    confirmActionLoading,
    actionsDisabled,
    extendConfirmButtonStyle,
    extendStyle,
    scrollContent,
    errorText,
  } = props;
  const containerRef = useRef();

  const handleClose = () => {
    onClose();
  };

  useOnClickOutside(containerRef, handleClose, toggle);

  const divRef = useRef(null);

  useEffect(() => {
    // Scroll to the bottom when content changes
    if (divRef.current) {
      divRef.current.scrollTop = divRef.current.scrollHeight;
    }
  }, [scrollContent]);

  useEffect(() => {
    if (toggle) {
      divRef.current.scrollTop = divRef.current.scrollTo(0, 0);
    }
  }, [toggle]);

  return (
    <ModalContainer data-testid="modal-container" toggle={toggle} extendStyle={extendStyle}>
      {toggle && <PreventBodyScrolling isOpen={toggle} />}
      <ModalDialog data-testid="modal-card" ref={containerRef} size={size}>
        <ModalHeader data-testid="modal-header">
          <Title data-testid="modal-title">{title}</Title>
          <ModalCloseIcon data-testid="modal-close-icon" onClick={handleClose} />
        </ModalHeader>
        <ModalBody data-testid="modal-body" ref={divRef}>
          {children !== '' && children}
        </ModalBody>
        {!actionsDisabled && onConfirmHandler && (
          <ModalFooterWrapper>
            <ModalErrorText>{errorText}</ModalErrorText>
            <ModalActions data-testid="modal-actions">
              <ModalButton
                data-testid="modal-cancel-button"
                onClick={() => {
                  if (onCancelHandler !== undefined) onCancelHandler();
                }}
              >
                {cancelActionLabel || 'Cancel'}
              </ModalButton>
              <ModalConfirmButton
                extendStyle={extendConfirmButtonStyle}
                data-testid="modal-confirm-button"
                onClick={() => {
                  onConfirmHandler();
                }}
                disabled={disableConfirmButton}
              >
                {(confirmActionLoading && <Loader width={14} height={14} thickness={4} />) ||
                  confirmActionLabel ||
                  'Save'}
              </ModalConfirmButton>
            </ModalActions>
          </ModalFooterWrapper>
        )}
      </ModalDialog>
    </ModalContainer>
  );
};

export default Modal;

Modal.propTypes = {
  size: PropTypes.string,
  title: PropTypes.string,
  toggle: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onCancelHandler: PropTypes.func,
  cancelActionLabel: PropTypes.string,
  onConfirmHandler: PropTypes.func,
  confirmActionLabel: PropTypes.string,
  disableConfirmButton: PropTypes.bool,
  confirmActionLoading: PropTypes.bool,
  children: PropTypes.node,
  actionsDisabled: PropTypes.bool,
  extendConfirmButtonStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  extendStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  scrollContent: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  errorText: PropTypes.string,
};
Modal.defaultProps = {
  title: 'modal title',
  size: 'md',
  onClose: () => {},
  onCancelHandler: () => {},
  cancelActionLabel: 'Cancel',
  onConfirmHandler: () => {},
  confirmActionLabel: 'Save',
  disableConfirmButton: false,
  confirmActionLoading: false,
  children: '',
  actionsDisabled: false,
  extendConfirmButtonStyle: '',
  extendStyle: '',
  scrollContent: '',
  errorText: '',
};
